import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@ngneat/transloco';
import { SheetPickerGroup, WorkbookSheetSummary } from '../../services/workbook-sheets';

/**
 * First step of a multi-sheet or multi-file table upload: lists every selected file and
 * every sheet in it (name, size, a short preview; a CSV is one sheet) and lets the user
 * choose which become tables. Sheets that cannot become a table (blank header cell, no rows)
 * and files that could not be read are listed with the reason and cannot be picked.
 */
@Component({
    selector: 'app-workbook-sheet-picker',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './workbook-sheet-picker.component.html',
    styleUrls: ['./workbook-sheet-picker.component.scss'],
    imports: [CommonModule, MatButtonModule, MatCheckboxModule, MatIconModule, TranslocoModule],
})
export class WorkbookSheetPickerComponent implements OnChanges {
    /** One group per selected file; sheet `index` values run across every group. */
    @Input() groups: SheetPickerGroup[] = [];
    /** Indexes of the chosen sheets, in file and sheet order. */
    @Output() readonly confirmed = new EventEmitter<number[]>();
    @Output() readonly cancelled = new EventEmitter<void>();

    selected = new Set<number>();

    ngOnChanges(): void {
        // Every sheet that can become a table starts selected; the user unticks the rest.
        this.selected = new Set(this.selectableSheets.map(s => s.index));
    }

    /** More than one file: show a header per file and the multi-file wording. */
    get isMultiFile(): boolean {
        return this.groups.length > 1;
    }

    get sheetCount(): number {
        return this.groups.reduce((n, g) => n + g.sheets.length, 0);
    }

    get selectableSheets(): WorkbookSheetSummary[] {
        return this.groups.flatMap(g => this.selectableIn(g));
    }

    get allSelected(): boolean {
        return this.selectableSheets.length > 0 && this.selected.size === this.selectableSheets.length;
    }

    get someSelected(): boolean {
        return this.selected.size > 0 && !this.allSelected;
    }

    selectableIn(group: SheetPickerGroup): WorkbookSheetSummary[] {
        return group.sheets.filter(s => !s.error);
    }

    isGroupAllSelected(group: SheetPickerGroup): boolean {
        const sheets = this.selectableIn(group);
        return sheets.length > 0 && sheets.every(s => this.selected.has(s.index));
    }

    isGroupPartlySelected(group: SheetPickerGroup): boolean {
        return this.selectableIn(group).some(s => this.selected.has(s.index)) && !this.isGroupAllSelected(group);
    }

    isSelected(sheet: WorkbookSheetSummary): boolean {
        return this.selected.has(sheet.index);
    }

    toggle(sheet: WorkbookSheetSummary, checked: boolean): void {
        if (sheet.error) {
            return;
        }
        const next = new Set(this.selected);
        if (checked) {
            next.add(sheet.index);
        } else {
            next.delete(sheet.index);
        }
        this.selected = next;
    }

    toggleGroup(group: SheetPickerGroup, checked: boolean): void {
        const next = new Set(this.selected);
        for (const s of this.selectableIn(group)) {
            if (checked) {
                next.add(s.index);
            } else {
                next.delete(s.index);
            }
        }
        this.selected = next;
    }

    toggleAll(checked: boolean): void {
        this.selected = checked ? new Set(this.selectableSheets.map(s => s.index)) : new Set();
    }

    problemKey(sheet: WorkbookSheetSummary): string {
        return sheet.error === 'missing header'
            ? 'One or more columns in this sheet have no header name.'
            : 'This sheet has no data.';
    }

    confirm(): void {
        if (!this.selected.size) {
            return;
        }
        this.confirmed.emit([...this.selected].sort((a, b) => a - b));
    }

    trackByGroup = (i: number, g: SheetPickerGroup) => `${i}:${g.fileName}`;
    trackBySheet = (_: number, s: WorkbookSheetSummary) => s.index;
    trackByIndex = (i: number) => i;
}
